import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableUserCertificate, UpdatableUserCertificate } from "@nova/schemas"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"

export const invalidateUserCertificates = () => invalidatePilotTrackingQuery("user_certificates")

const createUserCertificate = (orgId: string, body: CreatableUserCertificate) => {
    return fetchApiGateway("post /org/:orgId/fleet/pilot/certificate", {
        body: body,
        params: { orgId },
    })
}

export const useCreateUserCertificate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-user-certificate",
        (key, { arg }: { arg: CreatableUserCertificate }) =>
            orgId ? createUserCertificate(orgId, arg) : undefined,
        { onSuccess: invalidateUserCertificates }
    )

    return {
        createUserCertificate: trigger,
        isCreating: isMutating,
    }
}

const updateUserCertificate = (
    orgId: string,
    certificateId: string,
    body: UpdatableUserCertificate
) => {
    return fetchApiGateway("patch /org/:orgId/fleet/pilot/certificate/:certificateId", {
        body,
        params: { orgId, certificateId },
    })
}

export const useUpdateUserCertificate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-user-certificate",
        (key, { arg }: { arg: { certificateId: string; data: UpdatableUserCertificate } }) =>
            orgId ? updateUserCertificate(orgId, arg.certificateId, arg.data) : undefined,
        { onSuccess: invalidateUserCertificates }
    )

    return {
        updateUserCertificate: trigger,
        isUpdating: isMutating,
    }
}

const deleteUserCertificate = (orgId: string, certificateId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/pilot/certificate/:certificateId", {
        params: { orgId, certificateId },
    })
}

export const useDeleteUserCertificate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-user-certificate",
        (key, { arg }: { arg: string }) => (orgId ? deleteUserCertificate(orgId, arg) : undefined),
        { onSuccess: invalidateUserCertificates }
    )

    return {
        deleteUserCertificate: trigger,
        isDeleting: isMutating,
    }
}

export const userCertificateS3Key = (orgId: string, userId: string, certificateId: string) => {
    return `public/org/${orgId}/assets/user/${userId}/certificates/${certificateId}`
}
